import _ from 'lodash'
import { withCompInfo } from '@wix/editor-elements-integrations'
import {
	BaseRepeaterCss,
	ColumnsRepeaterCss,
	FluidColumnsRepeaterCss,
	RepeaterDefinition,
	RepeaterProperties,
	ResponsiveRepeaterCss,
} from './Repeater'
import { MediaContainerDefinition } from '@wix/thunderbolt-components-native'
import { sizeToString } from '@wix/thunderbolt-becky-root'

const justifyContentOptions = {
	justify: 'space-between',
	right: 'flex-end',
	left: 'flex-start',
	center: 'center',
} as const

const getJustifyContent = (compProps: RepeaterProperties) => {
	const alignment = compProps.alignment
	return justifyContentOptions[alignment]
}

const isJustifyAlignment = (compProps: RepeaterProperties) => compProps.alignment === 'justify'

const getDirection = (compProps: RepeaterProperties) => compProps.direction || 'ltr'

const getGap = (compProps: RepeaterProperties) => {
	const { gap } = compProps
	const isJustify = isJustifyAlignment(compProps)
	const horizontalGap = isJustify ? 0 : (gap.horizontal || 0) / 2
	const verticalGap = (gap.vertical || 0) / 2

	return {
		horizontalGap,
		verticalGap,
	}
}

const getCssMargin = (
	compProps: RepeaterProperties,
	isFluid: boolean,
	childDesign: MediaContainerDefinition['design']
) => {
	const { verticalGap, horizontalGap } = getGap(compProps)
	const borderSize = _.get(childDesign, ['cssStyle', 'cssBorder', 'width', 'left', 'value'], 0)
	const marginHorizontalStart = -(horizontalGap + borderSize)
	const marginHorizontalEnd = -horizontalGap + borderSize
	const direction = getDirection(compProps)
	const isLtr = direction === 'ltr'
	if (isFluid && borderSize !== 0) {
		const marginTop = -(verticalGap + borderSize)
		const marginBottom = -(verticalGap + borderSize)
		const marginRight = isLtr ? marginHorizontalEnd : marginHorizontalStart
		const marginLeft = isLtr ? marginHorizontalStart : marginHorizontalEnd
		return `${marginTop}px ${marginRight}px ${marginBottom}px ${marginLeft}px`
	} else {
		return `${-verticalGap}px ${-horizontalGap}px`
	}
}

const getBaseNonResponsiveCss = (
	compProps: RepeaterProperties,
	isFluid: boolean,
	childDesign: MediaContainerDefinition['design']
): BaseRepeaterCss => {
	const { verticalGap, horizontalGap } = getGap(compProps)
	return {
		'--direction': getDirection(compProps),
		'--justify-content': getJustifyContent(compProps),
		'--margin': getCssMargin(compProps, isFluid, childDesign),
		'--item-margin': `${verticalGap}px ${horizontalGap}px`,
	}
}

export const css = withCompInfo<
	ColumnsRepeaterCss | FluidColumnsRepeaterCss | ResponsiveRepeaterCss,
	RepeaterDefinition,
	{
		childData: { childDesign: MediaContainerDefinition['design'] }
	}
>()(
	['compProps', 'hasResponsiveLayout', 'compLayout', 'compSingleLayout', 'isOneDocMigrated'],
	({ compProps, hasResponsiveLayout, compLayout, compSingleLayout, isOneDocMigrated }, { childData }) => {
		if (hasResponsiveLayout) {
			return {}
		}
		const isFluid = isOneDocMigrated
			? compSingleLayout?.itemLayout?.type === 'MeshItemLayout' &&
			  compSingleLayout?.itemLayout.justifySelf === 'stretch'
			: !!compLayout.docked
		const baseNonResponsiveCss = getBaseNonResponsiveCss(compProps, isFluid, childData.childDesign)

		const dockedMargin = isOneDocMigrated
			? sizeToString(_.get(compSingleLayout, ['itemLayout', 'margins', 'left'], 0))
			: `${_.get(compLayout, ['docked', 'left', 'px'], 0)}px`

		if (isFluid) {
			return {
				...baseNonResponsiveCss,
				'--docked-margin': dockedMargin,
			}
		} else {
			return baseNonResponsiveCss
		}
	}
)
